import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError, getBaseId } from "@/lib/api";
import panelApi from "@/lib/panelApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CATS = ["Ciclista CLT", "Motoboy CLT", "Administrativo CLT", "Ciclista PJ", "Motoboy PJ", "Outro"];
const guessCat = (c) => {
  const pj = String(c.contract_type || "").toLowerCase() === "pj";
  if (c.role === "funcionario" || c.category === "administrativo") return "Administrativo CLT";
  return `${c.vehicle_type === "motociclista" ? "Motoboy" : "Ciclista"} ${pj ? "PJ" : "CLT"}`;
};
const selCls = "h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100";

export function AddThirteenthCollabDialog({ open, onClose, year, existingIds, onAdded }) {
  const [mode, setMode] = useState("db");
  const [form, setForm] = useState({ user_id: "", name: "", category: "Ciclista CLT", salary: "", admission: "" });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const all = useQuery({ queryKey: ["all-collabs-nobase"], enabled: open, queryFn: async () => (await api.get("/admin/collaborators", { skipBaseId: true })).data?.collaborators || [] });
  const list = useMemo(() => (all.data || []).filter((c) => !existingIds.has(c.id) && String(c.name || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => String(a.name).localeCompare(String(b.name), "pt-BR")), [all.data, existingIds, q]);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const pick = (id) => {
    const c = (all.data || []).find((x) => x.id === id);
    set(c ? { user_id: c.id, name: c.name, category: guessCat(c), salary: c.salary_override != null ? String(c.salary_override * 2) : form.salary } : { user_id: "" });
  };
  const submit = async () => {
    if (!form.name.trim() || form.salary === "" || Number(form.salary) < 0) { toast.error("Informe nome e base de cálculo"); return; }
    setBusy(true);
    try {
      const { data } = await panelApi.post("/panel/thirteenth-extras", { year, user_id: mode === "db" ? form.user_id : null, name: form.name.trim(), category: form.category, salary: Number(form.salary), base: getBaseId() || "" });
      if (form.admission) await panelApi.put(`/panel/admissions/${data.extra.user_id}`, { date: form.admission });
      onAdded(data.extra, form.admission || null);
      toast.success("Colaborador adicionado ao 13º");
      setForm({ user_id: "", name: "", category: "Ciclista CLT", salary: "", admission: "" }); setQ(""); setMode("db");
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setMode("db"); onClose(); } }}>
      <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="add13-dialog">
        <DialogHeader><DialogTitle>Adicionar colaborador ao 13º</DialogTitle></DialogHeader>
        <div className="flex overflow-hidden rounded-lg border border-slate-700 text-sm">
          {[["db", "Do cadastro"], ["manual", "Fora do cadastro"]].map(([v, l]) => (
            <button key={v} onClick={() => { setMode(v); set({ user_id: "", name: "" }); }} data-testid={`add13-mode-${v}`}
              className={`flex-1 px-3 py-1.5 transition-colors ${mode === v ? "bg-orange-500 text-white" : "bg-slate-950 text-slate-300 hover:bg-slate-800"}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-3">
          {mode === "db" ? (
            <div className="space-y-1.5">
              <Label>Colaborador (todas as bases)</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pelo nome" className="border-slate-700 bg-slate-950" data-testid="add13-search" />
              <div className="max-h-44 overflow-y-auto rounded-md border border-slate-700 bg-slate-950 p-1" role="listbox" data-testid="add13-collab-select">
                {all.isLoading && <p className="px-2 py-1.5 text-sm text-slate-500">Carregando…</p>}
                {!all.isLoading && !list.length && <p className="px-2 py-1.5 text-sm text-slate-500">Nenhum colaborador encontrado</p>}
                {list.map((c) => (
                  <button type="button" key={c.id} role="option" aria-selected={form.user_id === c.id} onClick={() => pick(c.id)} data-testid={`add13-collab-${c.id}`}
                    className={`block w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${form.user_id === c.id ? "bg-orange-500 text-white" : "text-slate-200 hover:bg-slate-800"}`}>
                    {c.name} · {guessCat(c)}
                  </button>
                ))}
              </div>
              {form.user_id && <p className="text-xs text-emerald-400" data-testid="add13-selected">Selecionado: {form.name}</p>}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Nome completo" className="border-slate-700 bg-slate-950" data-testid="add13-name" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <select value={form.category} onChange={(e) => set({ category: e.target.value })} className={selCls} data-testid="add13-category">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Base de cálculo (R$)</Label>
              <Input type="number" step="0.01" min="0" value={form.salary} onChange={(e) => set({ salary: e.target.value })} className="border-slate-700 bg-slate-950" data-testid="add13-salary" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Admissão (opcional)</Label>
            <Input type="date" value={form.admission} onChange={(e) => set({ admission: e.target.value })} className="border-slate-700 bg-slate-950 [color-scheme:dark]" data-testid="add13-admission" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 text-slate-300" onClick={onClose} data-testid="add13-cancel">Cancelar</Button>
          <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy || (mode === "db" && !form.user_id)} onClick={submit} data-testid="add13-submit">Adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
